import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import React, { useState, useEffect } from "react";
import MUITimePicker from "./MUITimePicker";
import { singleUser, updateWorkingHours } from "../../../services/userService";
import { useAlert } from "../../../contexts/AlertContext";
import { DateTime } from "luxon";

const defaultWorkingHours = [
  { day: "Monday", start_time: null, end_time: null },
  { day: "Tuesday", start_time: null, end_time: null },
  { day: "Wednesday", start_time: null, end_time: null },
  { day: "Thursday", start_time: null, end_time: null },
  { day: "Friday", start_time: null, end_time: null },
  { day: "Saturday", start_time: null, end_time: null },
  { day: "Sunday", start_time: null, end_time: null },
];

/** Read from DB (UTC "HH:mm:ss") -> JS Date aligned to `tz` for MUI */
const utcStringToTZDate = (utcTimeStr, tz) => {
  if (!utcTimeStr) return null;

  const [h, m, s] = utcTimeStr.split(":").map(Number);

  // Interpret it as UTC first
  const utc = DateTime.fromObject(
    { hour: h, minute: m, second: s || 0 },
    { zone: "utc" }
  );

  // Convert to target zone
  const zoned = utc.setZone(tz);

  // Build a *local* JS Date with the wall clock values of zoned time
  return new Date(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second
  );
};

/** ✅ CRITICAL FIX: interpret the picked clock time as time IN the selected TZ, then send UTC */
const pickedDateToUTCString = (picked /* JS Date from MUI */, tz) => {
  if (!picked) return null;

  // Time parts the user selected (as clock time, ignore the picked date/timezone)
  const hh = picked.getHours();
  const mm = picked.getMinutes();
  const ss = picked.getSeconds ? picked.getSeconds() : 0;

  const effectiveTZ = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Anchor to "today" in the effective TZ so DST is correct now
  const anchor = DateTime.now().setZone(effectiveTZ);

  // Build a wall-clock time in the selected timezone
  const zonedWallClock = DateTime.fromObject(
    {
      year: anchor.year,
      month: anchor.month,
      day: anchor.day,
      hour: hh,
      minute: mm,
      second: ss,
    },
    { zone: effectiveTZ }
  );

  // Convert that wall-clock time to UTC for storage
  return zonedWallClock.toUTC().toFormat("HH:mm:ss");
};

const CompanyDetails = ({ auth }) => {
  const { showAlert } = useAlert();
  const [is24HourOpen, setIs24HourOpen] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [isAllDaysSame, setIsAllDaysSame] = useState(false);
  const [workingHourData, setWorkingHourData] = useState([]);
  const [errors, setErrors] = useState({});
  const [userData, setUserData] = useState([]);
  const [weekOffDays, setWeekOffDays] = useState([]);

  useEffect(() => {
    singleUser(auth?.user?.id).then((response) => {
      if (response?.data?.user) {
        const tempUserData = response.data.user;
        setUserData(tempUserData);

        if (tempUserData?.timezone) setTimezone(tempUserData.timezone);

        if (tempUserData?.companyHours?.length) {
          const tz =
            tempUserData?.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          const tempHours = [...defaultWorkingHours];
          console.log("tempHours11", tempHours);

          tempUserData.companyHours.forEach((item) => {
            tempHours[item.day - 1].start_time = utcStringToTZDate(
              item?.start_time ?? "00:00:00",
              tz
            );
            tempHours[item.day - 1].end_time = utcStringToTZDate(
              item?.end_time ?? "00:00:00",
              tz
            );
          });
          console.log("tempHours11", tempHours, tz);

          const is247 = tempHours.every((item) => {
            if (!item.start_time || !item.end_time) return false;
            // Parse stored UTC values ("HH:mm:ss")
            // Convert JS Dates to Luxon in UTC
            const s = DateTime.fromJSDate(item.start_time, { zone: "utc" });
            const e = DateTime.fromJSDate(item.end_time, { zone: "utc" });

            // Extract only time-of-day in minutes
            const sMinutes = s.hour * 60 + s.minute;
            const eMinutes = e.hour * 60 + e.minute;

            // Case 1: 00:00 → 23:59 or 00:00 → 00:00 next day
            const coversFullDay =
              (sMinutes === 0 && e.hour === 23 && e.minute === 59) ||
              (sMinutes === 0 && eMinutes === 0);

            // Case 2: span is >= 24h (e.g. 04:00 → 03:59 next day)
            let diffMinutes = e.diff(s, "minutes").minutes;
            if (diffMinutes < 0) {
              // Crossed midnight
              diffMinutes += 24 * 60;
            }
            const spansFullDay = diffMinutes >= 24 * 60 - 1; // allow 1 minute less

            return coversFullDay || spansFullDay;
          });
          setIs24HourOpen(is247);

          setWorkingHourData(tempHours);
        } else {
          setWorkingHourData(defaultWorkingHours);
        }
      }
    });
  }, [auth?.user?.id]);

  // All days same?
  useEffect(() => {
    if (workingHourData?.length) {
      const first = workingHourData[0];
      const allSame = workingHourData.every(
        (d) =>
          d.start_time?.getHours() === first.start_time?.getHours() &&
          d.start_time?.getMinutes() === first.start_time?.getMinutes() &&
          d.end_time?.getHours() === first.end_time?.getHours() &&
          d.end_time?.getMinutes() === first.end_time?.getMinutes()
      );
      setIsAllDaysSame(allSame);
    }
  }, [workingHourData]);

  const handleWeekOff = (day) => {
    setWorkingHourData((prev) =>
      prev.map((item) => {
        if (item.day === day) {
          const isWeekOff =
            DateTime.fromJSDate(item.start_time).toFormat("HH:mm:ss") ===
            "00:00:00" &&
            DateTime.fromJSDate(item.end_time).toFormat("HH:mm:ss") ===
            "00:00:00";

          return isWeekOff
            ? {
              ...item,
              start_time: null, // restore to editable state
              end_time: null,
            }
            : {
              ...item,
              start_time: DateTime.fromObject({
                hour: 0,
                minute: 0,
                second: 0,
              }).toJSDate(),
              end_time: DateTime.fromObject({
                hour: 0,
                minute: 0,
                second: 0,
              }).toJSDate(),
            };
        }
        return item;
      })
    );
  };

  const copyEffortsToOtherDays = () => {
    if (!workingHourData?.length) return;
    const monday = workingHourData[0];
    const updated = workingHourData.map((d, i) =>
      i === 0
        ? d
        : { ...d, start_time: monday.start_time, end_time: monday.end_time }
    );
    setWorkingHourData(updated);
    setIsAllDaysSame(true);
  };

  const handle24HourOpenSwitchChange = () => {
    const newVal = !is24HourOpen;
    let updated;
    if (newVal) {
      updated = workingHourData.map((item) => ({
        ...item,
        start_time: DateTime.fromObject({
          hour: 0,
          minute: 0,
          second: 0,
        }).toJSDate(),
        end_time: DateTime.fromObject({
          hour: 23,
          minute: 59,
          second: 59,
        }).toJSDate(),
      }));
      setIs24HourOpen(true);
    } else {
      updated = [...workingHourData];
      setIs24HourOpen(false);
    }

    setWorkingHourData(updated);
    // setIs24HourOpen(newVal);
  };

  const handleTimeChange = (day_name, field_name, newValue) => {
    setWorkingHourData((prev) =>
      prev.map((d) =>
        d.day === day_name ? { ...d, [field_name]: newValue } : d
      )
    );
  };

  const checkFormIsValid = () => {
    const tmp = {};

    workingHourData.forEach(({ day, start_time, end_time }) => {
      console.log("start_time", start_time);
      console.log("start_time1", end_time);
      if (start_time && end_time && (end_time != null || start_time != null)) {
        const s = DateTime.fromJSDate(start_time);
        const e = DateTime.fromJSDate(end_time);

        // compare by minutes from midnight
        const sMinutes = s.hour * 60 + s.minute;
        const eMinutes = e.hour * 60 + e.minute;

        if (!(sMinutes == 0 && eMinutes == 0) && sMinutes >= eMinutes) {
          tmp[day] = "Start time must be earlier than end time.";
        }
      } else if (start_time == null || end_time == null) {
        // only one side filled
        tmp[day] = "Both start and end time must be set.";
      }
    });

    setErrors(tmp);
    return Object.keys(tmp).length === 0;
  };

  const handleSubmit = async () => {
    if (!checkFormIsValid()) return;

    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;


    console.log('this is something went good -------------------->', workingHourData)
    const finalData = workingHourData.map((item, index) => ({
      day: index + 1,
      /** ✅ use the fixed converter that treats the picked time as in `tz` */
      start_time: pickedDateToUTCString(item.start_time, tz),
      end_time: pickedDateToUTCString(item.end_time, tz),
    }));

    updateWorkingHours({ slots: finalData })
      .then((res) =>
        showAlert({
          title: "Users Notification",
          message: res?.message ?? "something went wrong, try again later",
          variant: "success",
        })
      )
      .catch((error) =>
        showAlert({
          title: "Users Notification",
          message:
            error?.data?.message ?? "something went wrong, try again later",
          variant: "danger",
        })
      );
  };

  return (
    <>
      <div className="p-4 mt-2">
        <h2 className="fs-5 fw-semibold text-gray-800 mb-4"></h2>

        <Row className="mb-4">
          <Form.Group className="col-md-12">
            <Form.Check
              type="switch"
              id="custom-switch"
              label="Available 24/7"
              checked={is24HourOpen}
              onChange={handle24HourOpenSwitchChange}
            />
          </Form.Group>
        </Row>

        <Row className="gy-4">
          <Form.Group className="col-md-3">
            <Form.Label>Week Days</Form.Label>
          </Form.Group>
          <Form.Group className="col-md-3">
            <Form.Label>Start Time</Form.Label>
          </Form.Group>
          <Form.Group className="col-md-3">
            <Form.Label>End Time</Form.Label>
          </Form.Group>
        </Row>

        {workingHourData?.map((data, row) => (
          <Row
            className="working-hour-row align-items-center gx-3 mb-4"
            key={data.day}
          >
            <Form.Group className="col-md-3">
              <Form.Control type="text" value={data.day} disabled />
            </Form.Group>

            <Form.Group className="col-md-3">
              <MUITimePicker
                disabled={
                  is24HourOpen ||
                  (data.start_time &&
                    data.end_time &&
                    DateTime.fromJSDate(data.start_time).toFormat(
                      "HH:mm:ss"
                    ) === "00:00:00" &&
                    DateTime.fromJSDate(data.end_time).toFormat("HH:mm:ss") ===
                    "00:00:00")
                }
                currentValue={data.start_time}
                onChange={(v) => handleTimeChange(data.day, "start_time", v)}
              />

              <span className="text-danger">{errors[data?.day]}</span>
            </Form.Group>

            <Form.Group className="col-md-3">
              <MUITimePicker
                disabled={
                  is24HourOpen ||
                  (data.start_time &&
                    data.end_time &&
                    DateTime.fromJSDate(data.start_time).toFormat(
                      "HH:mm:ss"
                    ) === "00:00:00" &&
                    DateTime.fromJSDate(data.end_time).toFormat("HH:mm:ss") ===
                    "00:00:00")
                }
                currentValue={data.end_time}
                onChange={(v) => handleTimeChange(data.day, "end_time", v)}
              />
            </Form.Group>

            {row === 0 && (
              <Form.Group className="col-md-3">
                <Form.Check
                  id="all-days-same"
                  type="checkbox"
                  className="mb-0"
                  label="Repeat for all days"
                  checked={isAllDaysSame}
                  onChange={copyEffortsToOtherDays}
                />
              </Form.Group>
            )}

            {row >= workingHourData.length - 2 && (
              <Form.Group className="col-md-3">
                <Form.Check
                   id={`week-off-${data.day}`}
                  type="checkbox"
                  disabled={is24HourOpen}
                  className="mb-0"
                  label="Week Off"
                  checked={
                    data.start_time &&
                    data.end_time &&
                    DateTime.fromJSDate(data.start_time).toFormat(
                      "HH:mm:ss"
                    ) === "00:00:00" &&
                    DateTime.fromJSDate(data.end_time).toFormat("HH:mm:ss") ===
                    "00:00:00"
                  }
                  onChange={() => handleWeekOff(data.day)}
                />
              </Form.Group>
            )}
          </Row>
        ))}
      </div>

      <div className="input-group-warpper d-flex align-align-items-center gap-2 p-4 mt-3">
        <Button
          variant="primary"
          className="btn-md"
          type="submit"
          onClick={handleSubmit}
        >
          Save & Update
        </Button>
      </div>
    </>
  );
};

export default CompanyDetails;
